import TagInput from "/WebInterface/new-ui/modules/tag-input/tag-input.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { ajax } from "/WebInterface/new-ui/assets/js/utils/ajax.js";

/**
 * Generates HTML markup for a form field based on the provided formField object.
 * @param {Object} formField - The form field object containing the field properties.
 * @returns {string} - The generated HTML markup for the form field.
 */
const generateFormField = (formField) => {
  // Extracting properties from formField object
  const {
    required,
    label,
    value,
    name,
    type,
    size,
    max_chars,
    cols,
    rows,
    options,
  } = formField;

  let html = "";
  let requiredField =
    required === "true" ? "<span class='required-field'>*</span>" : "";
  const randomId = utils.getUId();
  const encodedLabel = utils.sanitizePure(label);
  const encodedValue = typeof value === "string" ? utils.textEncode(value) : "";
  const encodedName = utils.sanitizePure(name);

  // Generating HTML based on the type of form field
  switch (type) {
    case "label": {
      const rightSideLabel = utils.sanitizePure(value);

      // Generating label HTML markup
      let u_name_label = `label_${randomId}`;
      if (encodedName) u_name_label = encodedName;

      let rightPage = null;
      let leftPage = null;
      const indexOfGETURLRight = rightSideLabel.indexOf("{get:");

      if (indexOfGETURLRight > -1) {
        const indexOfEndGETURL = rightSideLabel.indexOf("}");
        rightPage = rightSideLabel.substring(
          indexOfGETURLRight + 5,
          indexOfEndGETURL
        );
      }

      const indexOfGETURLLeft = encodedLabel.indexOf("{get:");
      if (indexOfGETURLLeft > -1) {
        const indexOfEndGETURL = encodedLabel.indexOf("}");
        leftPage = encodedLabel.substring(
          indexOfGETURLLeft + 5,
          indexOfEndGETURL
        );
      }

      html += `
        <div class="custom-form-field py-2${
          !rightSideLabel || leftPage ? " full-span" : ""
        }${leftPage ? " loadPage" : ""}" ${
        leftPage ? ` page='${leftPage}'` : ""
      }>
          <label class="text-primaryMain" name="meta_${u_name_label}">
            ${encodedLabel} ${requiredField}
          </label>
        </div>`;

      if (rightSideLabel) {
        // Generating value label HTML markup if value is provided
        html += `
          <div class="custom-form-field py-2${
            rightPage ? " loadPage full-span" : ""
          }" ${rightPage ? ` page='${rightPage}'` : ""}>
            <label class="text-primaryMain" name="meta_${u_name_label}">
              ${rightSideLabel}
            </label>
          </div>`;
      }
      break;
    }

    case "text": {
      // Generating text input HTML markup
      let inputType = "text";
      let validateType = "required";
      const inputSize = utils.textEncode(size);
      const inputMaxChars = utils.textEncode(max_chars);
      const inputWidthStyle = inputSize
        ? inputSize.toString().indexOf("%") > 0
          ? `style='width:${inputSize.toString()};'`
          : `style='width:${inputSize.toString().split("px")[0]}px;'`
        : "";

      // Checking for specific input types based on the name
      if (
        encodedName.lastIndexOf("_password") >= 0 ||
        encodedName.lastIndexOf("password") >= 0
      ) {
        inputType = "password";
      }

      if (
        encodedName.lastIndexOf("_email") >= 0 ||
        encodedName.lastIndexOf("email") >= 0
      ) {
        validateType = "email";
      }

      if (
        encodedName.lastIndexOf("_date1") >= 0 ||
        encodedName.lastIndexOf("_date") >= 0
      ) {
        inputType = "date";
      }

      html += `
        <div class="custom-form-field py-2">
          <label class="text-bold text-primaryMain" for="meta_${encodedName}">
            ${encodedLabel} ${requiredField}
          </label>
        </div>
        <div class="custom-form-field">
          <input id='custom-form-date-${encodedName}' class="input" name="meta_${encodedName}" type="${inputType}" ${inputWidthStyle}
            ${
              inputType === "date" && encodedName.lastIndexOf("_date1") >= 0
                ? "crushftp-future-date='true'"
                : ""
            }
            ${value ? `value="${encodedValue}"` : ""}
            ${inputMaxChars ? `maxlength="${inputMaxChars}"` : ""}
            ${
              requiredField
                ? `data-validate="${validateType}" data-mark-error="true" data-inline-error="true"`
                : ""
            }
          />
        </div>`;
      break;
    }

    case "textarea": {
      // Generating textarea HTML markup
      let textareaCols = parseInt(cols);
      if (textareaCols > 40) textareaCols = 40;

      html += `
        <div class="custom-form-field py-2">
          <label class="text-bold text-primaryMain" for="meta_${encodedName}">
            ${encodedLabel} ${requiredField}
          </label>
        </div>
        <div class="custom-form-field">
          <textarea class="textarea" name="meta_${encodedName}" cols="${textareaCols}" rows="${rows}"
            ${
              requiredField
                ? 'data-validate="required" data-mark-error="true" data-inline-error="true"'
                : ""
            }>${encodedValue}</textarea>
        </div>`;
      break;
    }

    case "combo": {
      // Generating combo box HTML markup
      const comboOptions = options.options_subitem;
      let comboOptionsHTML = "";

      comboOptions.forEach((option) => {
        let value = "";
        let label = "";
        const splittedValue = option.split(":");
        if (splittedValue.length > 1) {
          label = splittedValue[0];
          value = splittedValue[1];
        } else {
          label = splittedValue[0];
          value = splittedValue[0];
        }
        comboOptionsHTML += `<option value="${value}">${label}</option>`;
      });

      html += `
        <div class="custom-form-field py-2">
          <label class="text-bold text-primaryMain" for="meta_${encodedName}">
            ${encodedLabel} ${requiredField}
          </label>
        </div>
        <div class="custom-form-field">
          <div class="select">
            <select name="meta_${encodedName}" class="primary-select">
              ${comboOptionsHTML}
            </select>
          </div>
        </div>`;
      break;
    }

    case "checkbox": {
      // Generating checkbox group HTML markup
      let checkboxOptions = options.options_subitem;

      if (!Array.isArray(checkboxOptions)) {
        checkboxOptions = [checkboxOptions];
      }

      let checkboxOptionsHTML = "";

      checkboxOptions.forEach((option) => {
        checkboxOptionsHTML += `
          <div class="is-flex is-align-items-center">
            <input id="meta_${encodedName}_option_${option}" name="meta_${encodedName}" type="checkbox" value="${option}" />
            <label class="text-sm text-primaryMain px-2" for="meta_${encodedName}_option_${option}">${option}</label>
          </div>`;
      });

      html += `
        <div class="custom-form-field py-2">
          <label class="text-bold text-primaryMain">${encodedLabel} ${requiredField}</label>
        </div>
        <div class="custom-form-field">
          <div ${
            requiredField
              ? 'data-validate="checkbox-group" data-mark-error="true" data-inline-error="true"'
              : ""
          } class="py-2 is-flex is-flex-wrap-wrap checkbox-parent">
            ${checkboxOptionsHTML}
          </div>
        </div>`;
      break;
    }

    case "tags": {
      // Generating tags input HTML markup
      const tagsOptions = options.options_subitem;

      html += `
        <div class="custom-form-field py-2">
          <label class="text-bold text-primaryMain" for="meta_${name}">
            ${encodedLabel} ${requiredField}
          </label>
        </div>
        <div class="custom-form-field">
          <w-autocomplete-input
            name="meta_${encodedName}"
            options="${tagsOptions.join(",")}"
            data-parent=".modal .modal-card-body"
            ${
              requiredField
                ? 'data-validate="required" data-mark-error="true" data-inline-error="true"'
                : ""
            }
          ></w-autocomplete-input>
        </div>`;
      break;
    }

    case "radio": {
      // Generating radio button group HTML markup
      const radioOptions = options.options_subitem;
      let radioOptionsHTML = "";

      radioOptions.forEach((option, index) => {
        radioOptionsHTML += `
          <div class="is-flex is-align-items-center">
            <input type="radio" name="meta_${encodedName}" id="meta_${encodedName}_option_${option}" ${
          index === 0 ? "checked" : ""
        } value="${option}" />
            <label for="meta_${encodedName}_option_${option}" class="text-sm text-primaryMain px-2">${option}</label>
          </div>`;
      });

      html += `
        <div class="custom-form-field py-2">
          <label class="text-bold text-primaryMain">${encodedLabel} ${requiredField}</label>
        </div>
        <div class="custom-form-field py-2 is-flex is-flex-wrap-wrap">
          ${radioOptionsHTML}
        </div>`;
      break;
    }

    default:
      break;
  }

  return html;
};

/**
 * Generates a custom form based on the provided form information.
 * @param {object} formInfo - The form information containing the entries and their properties.
 * @returns {string} The generated HTML content for the custom form.
 */
const generateCustomForm = (formInfo) => {
  let formFields = formInfo.entries.entries_subitem;

  if (!Array.isArray(formFields)) {
    formFields = [formFields];
  }

  let htmlContent = `
    <div class="is-flex is-flex-direction-column is-align-items-center custom-form">
      <form class="grid-column-gap">`;

  formFields.forEach((fieldInfo) => {
    htmlContent += generateFormField(fieldInfo);
  });

  htmlContent += `
      </form>
    </div>`;

  return htmlContent;
};

/**
 * Attaches date picker functionality to input elements with type "date" within the specified parent.
 * @param {string|HTMLElement} parent - The parent element or selector within which to attach the date picker.
 */
const attachDatePicker = (parent = document.body) => {
  let parentElement = parent;

  const customDateFormat = utils.getUserCustomizationValue(
    "DATE_FORMAT_TEXT",
    true
  );

  let dateFormat = null;
  if (customDateFormat) {
    dateFormat = utils.dateStringFormat({ format: customDateFormat });
  }

  if (typeof parent === "string") {
    parentElement = document.querySelector(parent);
  }

  const inputDateType = parentElement.querySelectorAll('[type="date"]');

  inputDateType.forEach((element) => {
    const isFutureDate = element.getAttribute("crushftp-future-date");

    let options = {
      dateFormat: dateFormat || "m/d/Y",
    };

    if (isFutureDate) {
      const date = new Date();
      date.setDate(date.getDate() + 1);

      options = { ...options, minDate: date };
    }

    const calendar = flatpickr(`#${element.id}`, options);
    const value = element.value;

    if (value) {
      const date = new Date(value);
      calendar.setDate(date);
    }
  });
};

const applyCascadeOnForm = (parent = document.body) => {
  let parentElement = parent;

  if (typeof parent === "string") {
    parentElement = document.querySelector(parent);
  }

  parentElement = _$(parentElement);

  parentElement.find("[name*='_cascade']").each((element) => {
    const cascadeElement = _$(element);
    cascadeElement.on("change", () => {
      let optionList = [];
      let val = cascadeElement.val();
      const name = cascadeElement.attr("name");

      if (cascadeElement.is("select")) {
        if (!Array.isArray(val)) val = [val];

        cascadeElement.find("option").each((option) => {
          parentElement
            .find(
              `${`*[name$='${_$(option).attr("value")}_not']`}, ${`*[for$='${_$(
                option
              ).attr("value")}_not']`}`
            )
            .each((option) => {
              _$(option)
                .selfOrParent(".custom-form-field")
                .classList.remove("is-hidden");

              if (
                (_$(option).attr("name")?.indexOf("_cascade") > 0 &&
                  _$(option).attr("name") != name) ||
                (_$(option).attr("for")?.indexOf("_cascade") > 0 &&
                  _$(option).attr("for") != name)
              ) {
                optionList.push(option);
              }
            });
        });

        cascadeElement.find("option").each((option) => {
          parentElement
            .find(
              `${`*[name$='_${_$(option).val()}']`}, ${`*[for$='_${_$(
                option
              ).val()}']`}`
            )
            .each((option) => {
              _$(option)
                .selfOrParent(".custom-form-field")
                .classList.add("is-hidden");
            });
        });

        for (let i = 0; i < val.length; i++) {
          let curVal = val[i];
          parentElement
            .find(`${`*[name$='_${curVal}']`}, ${`*[for$='_${curVal}']`}`)
            .each((element) => {
              _$(element)
                .selfOrParent(".custom-form-field")
                .classList.remove("is-hidden");

              if (
                (_$(element).attr("name")?.indexOf("_cascade") > 0 &&
                  _$(element).attr("name") != name) ||
                (_$(element).attr("for")?.indexOf("_cascade") > 0 &&
                  _$(element).attr("for") != name)
              ) {
                optionList.push(element);
              }
            });
        }

        for (var i = 0; i < optionList.length; i++) {
          optionList[i].dispatchEvent(new Event("change"));
        }

        for (let i = 0; i < val.length; i++) {
          let curVal = val[i];
          parentElement
            .find(
              `${`*[name$='_${curVal}_not']`}, ${`*[for$='_${curVal}_not']`}`
            )
            .each((option) => {
              _$(option)
                .selfOrParent(".custom-form-field")
                .classList.add("is-hidden");
            });
        }
      } else {
        parentElement
          .find(`[name="${cascadeElement.attr("name")}"]`)
          .each((ele) => {
            parentElement
              .find(
                `${`*[name$="_${_$(ele).attr("value")}"]`}, ${`*[for$="_${_$(
                  ele
                ).attr("value")}"]`}`
              )
              .each((option) => {
                _$(option)
                  .selfOrParent(".custom-form-field")
                  .classList.add("is-hidden");
              });

            parentElement
              .find(
                `${`*[name$="_${_$(ele).attr(
                  "value"
                )}_not"]`}, ${`*[for$="_${_$(ele).attr("value")}_not"]`}`
              )
              .each((option) => {
                _$(option)
                  .selfOrParent(".custom-form-field")
                  .classList.remove("is-hidden");
              });
          });

        parentElement
          .find(`[name="${cascadeElement.attr("name")}"]:checked`)
          .each((ele) => {
            parentElement
              .find(
                `${`*[name$="_${_$(ele).attr("value")}"]`}, ${`*[for$="_${_$(
                  ele
                ).attr("value")}"]`}`
              )
              .each((option) => {
                _$(option)
                  .selfOrParent(".custom-form-field")
                  .classList.remove("is-hidden");
              });

            parentElement
              .find(
                `${`*[name$="_${_$(ele).attr(
                  "value"
                )}_not"]`}, ${`*[for$="_${_$(ele).attr("value")}_not"]`}`
              )
              .each((option) => {
                _$(option)
                  .selfOrParent(".custom-form-field")
                  .classList.add("is-hidden");
              });
          });
      }

      // parentElement
      //   .find("[name*='_cascade']:not(.chosen):hidden")
      //   .each((element) => {
      //     if (_$(element).is("select")) {
      //       _$(element)
      //         .find("option")
      //         .each((option) => {
      //           parentElement
      //             .find(
      //               `${`*[name$='${_$(element).attr(
      //                 "value"
      //               )}_not']`}, ${`*[for$='${_$(element).attr(
      //                 "value"
      //               )}_not']`}`
      //             )
      //             .each((option) => {
      //               _$(option)
      //                 .selfOrParent(".custom-form-field")
      //                 .classList.add("is-hidden");
      //             });

      //           parentElement
      //             .find(
      //               `${`*[name$='_${_$(option).val()}']`}, ${`*[for$='_${_$(
      //                 option
      //               ).val()}']`}`
      //             )
      //             .each((option) => {
      //               _$(option)
      //                 .selfOrParent(".custom-form-field")
      //                 .classList.add("is-hidden");
      //             });
      //         });
      //     } else {
      //       parentElement
      //         .find(
      //           `${`[name='${_$(element).attr("name")}']`}, ${`[for='${_$(
      //             element
      //           ).attr("name")}']`}`
      //         )
      //         .each((option) => {
      //           parentElement
      //             .find(
      //               `${`*[name$='_${_$(option).attr(
      //                 "value"
      //               )}']`}, ${`*[for$='_${_$(option).attr("value")}']`}`
      //             )
      //             .each((option) => {
      //               _$(option)
      //                 .selfOrParent(".custom-form-field")
      //                 .classList.add("is-hidden");
      //             });
      //           parentElement
      //             .find(
      //               `${`*[name$='_${_$(option).attr(
      //                 "value"
      //               )}_not']`}, ${`*[for$='_${_$(option).attr(
      //                 "value"
      //               )}_not']`}`
      //             )
      //             .each((option) => {
      //               _$(option)
      //                 .selfOrParent(".custom-form-field")
      //                 .classList.add("is-hidden");
      //             });
      //         });
      //     }
      //   });
    });
    cascadeElement.el().dispatchEvent(new Event("change"));
  });

  parentElement.find("input").each((element) => {
    const name = element.name;
    if (
      name.indexOf("verify") >= 0 &&
      parentElement.find(`input[name='${name}2']`).length > 0
    ) {
      parentElement
        .find(`input[name='${name}2']`)
        .attr("data-validate", "match")
        .attr("data-mark-error", "true")
        .attr("data-inline-error", "true")
        .attr("data-match", name);
    }
  });
};

const attachTagInputs = (parent = document.body, formName = "") => {
  let parentElement = parent;

  if (typeof parent === "string") {
    parentElement = document.querySelector(parent);
  }

  const textInputs = parentElement.querySelectorAll(
    'input[name*="_db"][type="text"]'
  );
  const tagInput = new TagInput();

  textInputs.forEach((element) => {
    const handleUsernameDebounce = utils.debounce((e) => {
      const value = e?.target?.value;
      if (!value) {
        tagInput.updateOptions({
          autoComplete: autoCompleteElement,
          helperText: t("typeInASearchTerm"),
        });
        return;
      }

      tagInput.updateOptions({
        autoComplete: autoCompleteElement,
        helperText: `${t("searching")}...`,
      });

      ajax
        .call({
          method: "GET",
          urlEncoded: true,
          data: {
            command: "lookup_form_field",
            form_name: formName,
            form_element_name: `${element.name}`,
            q: value,
          },
        })
        .then((res) => {
          setTimeout(() => {
            const usernames = res?.map((item) => {
              return item.name;
            });
            const usernamesString = usernames.join();

            tagInput.updateOptions({
              autoComplete: autoCompleteElement,
              options: usernamesString || value,
            });
          }, 500);
        });
    }, 500);

    const autoCompleteElement = tagInput.attach({
      input: `#${element.id}`,
      helperText: t("typeInASearchTerm"),
      onInputChange: handleUsernameDebounce,
    });
  });
};

const attachCustomFormTools = (parentElement, formName) => {
  attachDatePicker(parentElement);
  applyCascadeOnForm(parentElement);
  attachTagInputs(parentElement, formName);

  const firstInput = parentElement.querySelector("input");

  if (firstInput) {
    firstInput.focus();
  }

  // Load pages in customform
  _$(parentElement)
    .find(".loadPage")
    .sel()
    .forEach((el) => {
      const page = _$(el).attr("page");
      if (page) {
        _$(el).html("");

        const element = _$("<div>");
        element.addClass("has-text-left");
        _$(el).append(element.el());

        if (page.indexOf("#") > 0) {
          let style = page.substring(page.indexOf("#") + 1, page.length);
          element.attr("style", style);
        }

        element.css("overflow", "auto");
        element.text("Wait..");

        ajax
          .call({
            method: "GET",
            url: page,
          })
          .then((response) => {
            element.html(response);
          })
          .catch((error) => {
            element.text("Error while loading: " + page);
          });
      }
    });
};

export { generateCustomForm, attachCustomFormTools };
